import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import type { AlertColor } from '@mui/material/Alert';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { makeAuthenticatedApiCall } from '../apiHelper';
import { useAuth } from '../components/AuthContext';
import { ErrorMessageSnackbar } from '../components/ErrorMessageSnackbar';
import { getAllFranchises, getFranchise } from '../utils';

// Note : le dropdown de sélection du modèle OCR (Sonnet/Opus/Haiku/Mistral/
// Qwen) a été retiré (juin 2026). L'extraction tourne désormais en dur sur
// Claude Sonnet 4.6 côté backend (cf. appSettingsModel.getCgOcrModel) — pour
// rebasculer ponctuellement, modifier la constante backend, plus via DB.

type FeatureFlagKey = 'form_cgia';

interface OrganizationWithFlags {
  organization_id: number;
  organization_name: string | null;
  franchise_id: string | null;
  flags: Partial<Record<FeatureFlagKey, boolean>>;
}

interface AdminFlagsResponse {
  success: boolean;
  data: { organizations: OrganizationWithFlags[] };
}

const ALL_FRANCHISES_VALUE = 'all';

const FeatureFlagsAdminPage = () => {
  const { currentUser } = useAuth();
  const userRole = currentUser?.data?.data?.role;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchText, setSearchText] = useState('');
  const [franchiseFilter, setFranchiseFilter] = useState<string>(ALL_FRANCHISES_VALUE);
  const [togglingOrgId, setTogglingOrgId] = useState<number | null>(null);
  const [togglingExternal, setTogglingExternal] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('success');

  // Redirection si non-god
  useEffect(() => {
    if (userRole && userRole !== 'god') {
      navigate('/');
    }
  }, [userRole, navigate]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['adminFeatureFlags'],
    queryFn: async () => {
      const response = await makeAuthenticatedApiCall<AdminFlagsResponse>('get', '/api/feature-flags/admin');
      return response.data;
    },
    enabled: userRole === 'god',
    staleTime: 0,
  });

  // Réglage global : cotation externe (lien public ?from=external)
  const { data: externalCotationData, isLoading: isExternalLoading } = useQuery({
    queryKey: ['externalCotationStatus'],
    queryFn: async () => {
      const response = await makeAuthenticatedApiCall<{ success: boolean; data: { enabled: boolean } }>(
        'get',
        '/api/app-settings/external-cotation',
      );
      return response.data;
    },
    enabled: userRole === 'god',
    staleTime: 0,
  });
  const externalCotationEnabled = externalCotationData?.data?.enabled ?? true;

  const organizations = data?.data?.organizations ?? [];
  const franchiseOptions = getAllFranchises();

  const openSnackbar = (message: string, severity: AlertColor) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const filteredOrganizations = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    return organizations.filter((org) => {
      const matchesFranchise =
        franchiseFilter === ALL_FRANCHISES_VALUE || (org.franchise_id ?? '') === franchiseFilter;
      if (!matchesFranchise) return false;

      if (!needle) return true;
      const name = (org.organization_name ?? '').toLowerCase();
      const franchiseName = getFranchise(org.franchise_id ?? undefined).toLowerCase();
      const idStr = String(org.organization_id);
      return name.includes(needle) || franchiseName.includes(needle) || idStr.includes(needle);
    });
  }, [organizations, searchText, franchiseFilter]);

  const handleToggleFlag = async (organizationId: number, flagKey: FeatureFlagKey, nextValue: boolean) => {
    setTogglingOrgId(organizationId);
    try {
      await makeAuthenticatedApiCall('put', `/api/feature-flags/admin/${organizationId}`, {
        flagKey,
        enabled: nextValue,
      });
      await queryClient.invalidateQueries({ queryKey: ['adminFeatureFlags'] });
      openSnackbar(`FormCGIA ${nextValue ? 'activé' : 'désactivé'} pour cette agence`, 'success');
    } catch (error) {
      console.error('Toggle flag error:', JSON.stringify(error));
      openSnackbar('Erreur lors de la mise à jour du flag', 'error');
    } finally {
      setTogglingOrgId(null);
    }
  };

  const handleToggleExternalCotation = async (nextValue: boolean) => {
    setTogglingExternal(true);
    try {
      await makeAuthenticatedApiCall('put', '/api/app-settings/external-cotation', { enabled: nextValue });
      await queryClient.invalidateQueries({ queryKey: ['externalCotationStatus'] });
      openSnackbar(`Cotation externe ${nextValue ? 'activée' : 'désactivée'}`, 'success');
    } catch (error) {
      console.error('Toggle external cotation error:', JSON.stringify(error));
      openSnackbar('Erreur lors de la mise à jour de la cotation externe', 'error');
    } finally {
      setTogglingExternal(false);
    }
  };

  const handleBulkEnableForFranchise = async (enabled: boolean) => {
    if (franchiseFilter === ALL_FRANCHISES_VALUE) {
      openSnackbar('Sélectionnez d\'abord une franchise avant de faire une action groupée', 'warning');
      return;
    }
    const franchiseOrgs = organizations.filter((org) => (org.franchise_id ?? '') === franchiseFilter);
    if (franchiseOrgs.length === 0) {
      openSnackbar('Aucune agence dans cette franchise', 'info');
      return;
    }
    const confirmed = window.confirm(
      `${enabled ? 'Activer' : 'Désactiver'} FormCGIA pour ${franchiseOrgs.length} agences de la franchise "${getFranchise(franchiseFilter)}" ?`,
    );
    if (!confirmed) return;

    setTogglingOrgId(-1);
    try {
      // Boucle séquentielle pour éviter de spammer l'API
      for (const org of franchiseOrgs) {
        await makeAuthenticatedApiCall('put', `/api/feature-flags/admin/${org.organization_id}`, {
          flagKey: 'form_cgia',
          enabled,
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['adminFeatureFlags'] });
      openSnackbar(
        `FormCGIA ${enabled ? 'activé' : 'désactivé'} pour ${franchiseOrgs.length} agence(s)`,
        'success',
      );
    } catch (error) {
      console.error('Bulk toggle error:', JSON.stringify(error));
      openSnackbar('Erreur lors de la mise à jour groupée', 'error');
    } finally {
      setTogglingOrgId(null);
    }
  };

  if (userRole !== 'god') {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <ToggleOnIcon sx={{ fontSize: 40, color: '#FDBB5F' }} />
        <Typography variant="h1" sx={{ fontSize: 32 }}>
          Feature flags par agence
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h2" sx={{ fontSize: 20, mb: 1 }}>
          Réglages globaux
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ maxWidth: 640 }}>
            <Typography sx={{ fontWeight: 600 }}>Cotation externe (lien public)</Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Active le formulaire de cotation accessible via le lien externe (<code>?from=external</code>),
              dont les cotations sont rattachées au compte "Prospection Skipcar". Désactiver coupe la
              création de ces cotations : formulaire bloqué côté vendeur et refus côté serveur.
            </Typography>
          </Box>
          {isExternalLoading || togglingExternal ? (
            <CircularProgress size={24} sx={{ color: '#FDBB5F' }} />
          ) : (
            <Switch
              checked={externalCotationEnabled}
              onChange={(e) => handleToggleExternalCotation(e.target.checked)}
              disabled={togglingExternal}
            />
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
          Active ou désactive <strong>FormCGIA</strong> (nouveau formulaire de carte grise assisté par IA)
          pour chaque agence. Les agences avec le flag activé seront automatiquement redirigées vers la
          nouvelle version du formulaire lors de la création d'une demande de carte grise. Le provider IA
          utilisé est défini au-dessus.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Rechercher une agence"
            placeholder="Nom, franchise ou ID..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="small"
            sx={{ minWidth: 280 }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Franchise</InputLabel>
            <Select
              value={franchiseFilter}
              label="Franchise"
              onChange={(e) => setFranchiseFilter(e.target.value)}
            >
              <MenuItem value={ALL_FRANCHISES_VALUE}>Toutes les franchises</MenuItem>
              {franchiseOptions.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            color="success"
            disabled={franchiseFilter === ALL_FRANCHISES_VALUE || togglingOrgId !== null}
            onClick={() => handleBulkEnableForFranchise(true)}
          >
            Activer pour la franchise
          </Button>
          <Button
            variant="outlined"
            color="error"
            disabled={franchiseFilter === ALL_FRANCHISES_VALUE || togglingOrgId !== null}
            onClick={() => handleBulkEnableForFranchise(false)}
          >
            Désactiver pour la franchise
          </Button>
        </Box>
      </Paper>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress sx={{ color: '#FDBB5F' }} />
        </Box>
      )}

      {isError && (
        <Paper sx={{ p: 3 }}>
          <Typography color="error">Erreur lors du chargement des agences.</Typography>
        </Paper>
      )}

      {!isLoading && !isError && (
        <Paper>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: 14, color: '#666' }}>
              {filteredOrganizations.length} agence(s) affichée(s) sur {organizations.length}
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Nom de l'agence</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Franchise</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    FormCGIA
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrganizations.map((org) => {
                  const enabled = Boolean(org.flags.form_cgia);
                  const isToggling = togglingOrgId === org.organization_id;
                  return (
                    <TableRow key={org.organization_id} hover>
                      <TableCell>{org.organization_id}</TableCell>
                      <TableCell>{org.organization_name ?? '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={getFranchise(org.franchise_id ?? undefined)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {isToggling ? (
                          <CircularProgress size={20} sx={{ color: '#FDBB5F' }} />
                        ) : (
                          <Switch
                            checked={enabled}
                            onChange={(e) =>
                              handleToggleFlag(org.organization_id, 'form_cgia', e.target.checked)
                            }
                            disabled={togglingOrgId !== null}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <ErrorMessageSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        handleClose={() => setSnackbarOpen(false)}
      />
    </Container>
  );
};

export default FeatureFlagsAdminPage;
